//  Externals
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
//  Internals
import { URL } from "../../constants/URL";
import CS from "../../services/commonService";
import Theme from "../common/theme";
import { useVerifyResetToken, useUpdatePassword } from "../../queryHooks";
import { updatePasswordSchema } from "../../validations";
import { useStore } from "../../zustand/store";
//	Component starts
const ResetPassword = () => {
	//	Location
	const location = useLocation();
	// 	Navigation
	const navigate = useNavigate();
	//	State values
	const [hideConfirmPassword, setHideConfirmPassword] = useState(true);
	const [hideNewPassword, setHideNewPassword] = useState(true);
	const [token, setToken] = useState("");
	const setLoading = useStore((state) => state.setLoading);
	// Defining state object for formik
	const [formData] = useState({
		newPassword: "",
		confirmPassword: "",
	});

	/******************************************************************************************
	@Purpose    :   To initialize verify reset token query
	*******************************************************************************************/
	const {
		mutateAsync: verifyResetToken,
		error: errorVerifyResetToken,
		data: dataVerifyResetToken,
	} = useVerifyResetToken();

	/******************************************************************************************
	@Purpose    :   To initialize update password query
	*******************************************************************************************/
	const {
		mutateAsync: updatePassword,
		error: errorUpdatePassword,
		data: dataUpdatePassword,
	} = useUpdatePassword();

	/******************************************************************************************
	@Purpose    :   Validate and call update password
	*******************************************************************************************/
	const { values, errors, handleChange, handleSubmit, touched } = useFormik({
		initialValues: formData,
		validationSchema: updatePasswordSchema,
		onSubmit: () => {
			updatePassword({ new_password: values.newPassword, reset_token: token });
			setLoading(true);
		},
	});

	/******************************************************************************************
	@Purpose    :   Validate and call verify reset token
	*******************************************************************************************/
	useEffect(() => {
		let _forgotToken = window.location.pathname.split("reset-password/")[1];
		setToken(_forgotToken);
		verifyResetToken({ forgotToken: _forgotToken });
		setLoading(true);
	}, [location]);

	/******************************************************************************************
	@Purpose    :   To catch the response of verify reset token
	*******************************************************************************************/
	useEffect(() => {
		if (dataVerifyResetToken) {
			setLoading(false);
			if (dataVerifyResetToken.status === "success") {
				// CS.showSuccessMessage(dataVerifyResetToken.message);
			}
		}
	}, [dataVerifyResetToken]);

	useEffect(() => {
		if (errorVerifyResetToken) {
			setLoading(false);
			CS.showErrorMessage(errorVerifyResetToken.response?.data?.message);
			navigate(URL.login, { replace: true });
		}
	}, [errorVerifyResetToken]);

	/******************************************************************************************
	@Purpose    :   To catch the response of update password
	*******************************************************************************************/
	useEffect(() => {
		if (dataUpdatePassword) {
			setLoading(false);
			if (dataUpdatePassword.status === "success") {
				CS.showSuccessMessage(dataUpdatePassword.message);
				navigate(URL.login, { replace: true });
			}
		}
	}, [dataUpdatePassword]);

	useEffect(() => {
		if (errorUpdatePassword) {
			setLoading(false);
			CS.showErrorMessage(errorUpdatePassword.response?.data?.message);
		}
	}, [errorUpdatePassword]);

	/******************************************************************************************
	@Purpose    :   To show the tooltip for new password show/hid
	*******************************************************************************************/
	const renderTooltipForNewPassword = () =>
		hideNewPassword ? "Show Password" : "Hide Password";

	/******************************************************************************************
	@Purpose    :   To show the tooltip for old password show/hid
	*******************************************************************************************/
	const renderToolTipForOldPassword = () =>
		hideConfirmPassword ? "Show Password" : "Hide Password";

	return (
		<>
			<div
				className="login-wrapper"
				style={{
					background: "url(assets/images/login-bg.jpg) no-repeat center center",
					backgroundSize: "cover",
				}}
			>
				<div className="login-body">
					<section className="login-container row m-0">
						<div className="col-xl-8 col-11">
							<div className="card bg-login-card">
								<div className="row m-0">
									<div className="col-md-6 col-12 pl-0 pr-0">
										<div className="card rounded-left mb-0 p-4 d-flex justify-content-center h-100">
											<div className="card-header">
												<div className="card-title text-center mb-4">
													<h4 className="mb-2 mt-2">Reset Your Password</h4>
												</div>
											</div>
											<div className="card-content">
												<div className="card-body">
													<form autoComplete="off" onSubmit={handleSubmit}>
														<div className="form-group position-relative">
															<label className="label">
																New Password<sup className="text-danger">*</sup>
															</label>
															<input
																className="form-control"
																type={hideNewPassword ? "password" : "text"}
																placeholder="New Password"
																id="newPassword"
																name="newPassword"
																value={values.newPassword}
																onChange={handleChange}
															/>
															<span
																onClick={() =>
																	setHideNewPassword(!hideNewPassword)
																}
																data-tip
																data-for="newPassword"
																className={
																	hideNewPassword
																		? "bx bx-hide field-icon toggle-password"
																		: "bx bx-show field-icon toggle-password"
																}
															></span>
															<ReactTooltip
																id="newPassword"
																place="top"
																effect="solid"
															>
																{renderTooltipForNewPassword()}
															</ReactTooltip>
															<span className="text-danger error-msg">
																{errors.newPassword && touched.newPassword
																	? errors.newPassword
																	: null}
															</span>
														</div>
														<div className="form-group position-relative">
															<label className="label">
																Confirm Password
																<sup className="text-danger">*</sup>
															</label>
															<input
																className="form-control"
																type={hideConfirmPassword ? "password" : "text"}
																placeholder="Confirm Password"
																id="confirmPassword"
																name="confirmPassword"
																value={values.confirmPassword}
																onChange={handleChange}
															/>
															<span
																onClick={() =>
																	setHideConfirmPassword(!hideConfirmPassword)
																}
																data-tip
																data-for="confirmPassword"
																className={
																	hideConfirmPassword
																		? "bx bx-hide field-icon toggle-password"
																		: "bx bx-show field-icon toggle-password"
																}
															></span>
															<ReactTooltip
																id="confirmPassword"
																place="top"
																effect="solid"
															>
																{renderToolTipForOldPassword()}
															</ReactTooltip>
															<span className="text-danger error-msg">
																{errors.confirmPassword &&
																touched.confirmPassword
																	? errors.confirmPassword
																	: null}
															</span>
														</div>
														<div className="mt-5 text-center">
															<button
																className="btn btn-primary glow position-relative btn-block"
																type="submit"
															>
																Reset Password
															</button>
														</div>
													</form>
													<div className=" d-flex flex-md-row flex-column justify-content-between align-items-center mt-4 mb-3">
														<div className="text-left common-small-text">
															<small>
																{" "}
																<Link to={URL.login}>Sign in</Link>
															</small>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className="col-md-6 d-md-block d-none text-center align-self-center p-7">
										<div className="card-content">
											<img
												className="img-fluid"
												src="../assets/images/login.png"
												alt="login"
											></img>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</div>
			</div>
			<Theme />
		</>
	);
};

export default ResetPassword;
